import { useState } from 'react'

import { SettingsSwitch } from './SettingsSwitch'
import { useSidebarPrefs } from '../stories/useSidebarPrefs'

export function SettingsSidebarTab() {
  const { expanded, expandOnHover, setExpanded, setExpandOnHover } = useSidebarPrefs()
  const [error, setError] = useState<string | null>(null)

  const hoverDisabled = expanded

  const handleExpandOnHoverChange = (next: boolean) => {
    try {
      setExpandOnHover(next)
      setError(null)
    } catch {
      setError('Could not update sidebar preference. Try again.')
    }
  }

  const handleKeepExpandedChange = (next: boolean) => {
    try {
      setExpanded(next)
      setError(null)
    } catch {
      setError('Could not update sidebar preference. Try again.')
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-border-default bg-interactive-subtle p-4">
        <h3 className="mb-2 text-sm font-medium text-text-primary">How it works</h3>
        <ul className="list-inside list-disc space-y-1 text-xs text-text-secondary">
          <li>
            <strong>Date Settings:</strong> Apply to all jobs when using &quot;Auto&quot; mode for
            dates.
          </li>
          <li>
            <strong>Sidebar:</strong> Saved on this device only. Use the chevron above Profile to
            collapse or expand anytime.
          </li>
        </ul>
      </div>

      {error && (
        <div
          className="rounded border border-danger/20 bg-danger-subtle p-3 text-sm text-danger-strong"
          role="alert"
        >
          {error}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="space-y-6 rounded-lg border border-border-default bg-surface-default p-6 shadow-sm">
        <div>
          <h2 className="text-h3 font-semibold text-text-primary">Sidebar</h2>
          <p className="mt-1 text-xs text-text-secondary">
            Control how the left navigation rail opens and stays open.
          </p>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <label htmlFor="sidebar-expand-on-hover" className="text-sm font-medium text-text-primary">
              Expand sidebar on hover
            </label>
            <p id="sidebar-expand-on-hover-desc" className="mt-1 text-xs text-text-secondary">
              When the rail is collapsed, move the pointer over it to temporarily show labels.
              Moving away collapses it again unless you pinned it open with the chevron.
            </p>
            {hoverDisabled && (
              <p className="mt-2 text-xs text-text-muted">
                Not needed while the sidebar stays expanded.
              </p>
            )}
          </div>
          <SettingsSwitch
            id="sidebar-expand-on-hover"
            checked={expandOnHover}
            disabled={hoverDisabled}
            aria-describedby="sidebar-expand-on-hover-desc"
            onCheckedChange={handleExpandOnHoverChange}
          />
        </div>

        <div className="flex items-start justify-between gap-4 border-t border-border-default pt-6">
          <div className="flex-1">
            <label htmlFor="sidebar-keep-expanded" className="text-sm font-medium text-text-primary">
              Keep sidebar expanded
            </label>
            <p id="sidebar-keep-expanded-desc" className="mt-1 text-xs text-text-secondary">
              Keep the navigation rail at full width with labels visible. Turn off to use the
              compact icon rail.
            </p>
          </div>
          <SettingsSwitch
            id="sidebar-keep-expanded"
            checked={expanded}
            aria-describedby="sidebar-keep-expanded-desc"
            onCheckedChange={handleKeepExpandedChange}
          />
        </div>
      </div>
    </div>
  )
}
